import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation } from '@apollo/client';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { Button } from '../button';
import { Modal } from '../modal';
import { URLS } from '../../../constants/urls';
import {
  CHARGE_EXTRA_MILEAGE,
  COMPLETE_GUEST_CHECKOUT,
  COMPLETE_HOST_CHECKOUT,
  DELETE_TRIP_PHOTO,
  GET_PRESIGNED_URL_FOR_BOOKING,
  UPLOAD_TRIP_PHOTO,
} from '../../services/bookingService/queries';
import { sameBookingUserId } from '../../helpers/bookingUserId';
import { useSnapshot } from 'valtio';
import { authStore } from '../../../store/authStore';
import { useBookingLocale } from '../../hooks/useBookingLocale';
import { formatMoney } from '../../utils/money';

const MIN_PHOTOS = 6;
const FUEL_LEVELS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

const Container = styled.div`
  ${tw`flex flex-col gap-4 w-full`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold text-text-primary pr-8`}
`;

const StepHeader = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const Section = styled.div`
  ${tw`flex flex-col gap-3`}
`;

const Label = styled.label`
  ${tw`text-sm font-medium text-text-secondary`}
`;

const HelperText = styled.p`
  ${tw`text-xs text-text-secondary m-0`}
`;

const DropzoneContainer = styled.div<{ isDragActive: boolean }>`
  ${tw`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors max-w-full min-w-0 box-border`}
  border-color: ${({ isDragActive }) => (isDragActive ? '#4F46E5' : 'var(--color-border)')};
  background-color: ${({ isDragActive }) => (isDragActive ? '#EEF2FF' : 'white')};
`;

const PhotoGrid = styled.div`
  ${tw`grid gap-3 mt-2 overflow-y-auto p-2`}
  grid-template-columns: repeat(auto-fill, minmax(108px, 1fr));
  max-height: 280px;
`;

const PhotoPreview = styled.div`
  ${tw`relative aspect-square rounded-lg overflow-hidden border border-border-default`}
`;

const PhotoImg = styled.img`
  ${tw`w-full h-full object-cover`}
`;

const RemoveButton = styled.button`
  ${tw`absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold hover:bg-red-600 disabled:opacity-50`}
`;

const Input = styled.input`
  ${tw`w-full p-3 border border-border-default rounded-lg`}
`;

const FuelGrid = styled.div`
  ${tw`grid grid-cols-5 gap-2`}
`;

const FuelButton = styled.button<{ selected: boolean }>`
  ${tw`py-2 rounded-lg border text-sm font-medium`}
  border-color: ${({ selected }) =>
    selected ? 'var(--color-text-primary)' : 'var(--color-border)'};
  background: ${({ selected }) => (selected ? 'var(--color-text-primary)' : '#fff')};
  color: ${({ selected }) => (selected ? '#fff' : 'var(--color-text-secondary)')};
`;

const Actions = styled.div`
  ${tw`flex flex-col md:flex-row gap-3 justify-end pt-2`}
`;

const SummaryBox = styled.div`
  ${tw`rounded-lg border border-border-default bg-bg-secondary p-3 text-sm text-text-secondary flex flex-col gap-1`}
`;

const ChargeBanner = styled.div`
  ${tw`rounded-lg border p-4 flex flex-col gap-3`}
  background: #fff7ed;
  border-color: #fed7aa;
`;

interface PostTripReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: any;
  isHost: boolean;
  refetchBooking: () => Promise<any> | void;
}

export const PostTripReturnModal: React.FC<PostTripReturnModalProps> = ({
  isOpen,
  onClose,
  booking,
  isHost,
  refetchBooking,
}) => {
  // Spec 028: the odometer, allowance and extra distance are in the trip's unit.
  const { distanceUnit, currency } = useBookingLocale(booking?.id, isHost ? 'host' : 'guest');
  const km = distanceUnit === 'km';
  const money = (value: number) =>
    km || currency !== 'USD'
      ? formatMoney(value, currency, { withCode: true })
      : `$${value.toFixed(2)}`;
  const [stepIndex, setStepIndex] = useState(0);
  const [postTripMileage, setPostTripMileage] = useState('');
  const [postTripFuelLevel, setPostTripFuelLevel] = useState<number | null>(null);
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [overrideMiles, setOverrideMiles] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<number, number>>({});
  const [checkoutResult, setCheckoutResult] = useState<any>(null);

  const [getPresignedUrl] = useMutation(GET_PRESIGNED_URL_FOR_BOOKING);
  const [uploadTripPhoto] = useMutation(UPLOAD_TRIP_PHOTO);
  const [deleteTripPhoto] = useMutation(DELETE_TRIP_PHOTO);
  const [completeGuestCheckout, { loading: completingGuest }] =
    useMutation(COMPLETE_GUEST_CHECKOUT);
  const [completeHostCheckout, { loading: completingHost }] = useMutation(COMPLETE_HOST_CHECKOUT);
  const [chargeExtraMileage, { loading: chargingMileage }] = useMutation(CHARGE_EXTRA_MILEAGE);

  const auth = useSnapshot(authStore);
  /** Flutter compares `uploadedBy` to `authStore.user.id`. Web queries often omit root `guestId`/`hostId` — use nested guest/host ids. */
  const actorUserId =
    (isHost ? (booking?.hostId ?? booking?.host?.id) : (booking?.guestId ?? booking?.guest?.id)) ??
    auth.user?.id;
  /** Only server photos with a key/url; owner match uses string-normalized ids (GraphQL int vs string). */
  const uploadedTripEndImages = (booking?.tripEndImages || []).filter(
    (image: any) => image?.url != null && String(image.url).trim() !== '',
  );
  const ownPhotoCount = uploadedTripEndImages.filter(
    (image: any) => actorUserId != null && sameBookingUserId(image?.uploadedBy, actorUserId),
  ).length;
  const totalOwnPhotoCount = ownPhotoCount + selectedFiles.length;
  const canProceedFromPhotos =
    totalOwnPhotoCount >= MIN_PHOTOS && selectedFiles.length === 0 && !isUploading;
  const steps = isHost ? ['photos', 'details'] : ['photos'];
  const currentStep = steps[stepIndex];

  const preTripMileage = Number(booking?.preTripMileage || 0);
  const allowedMileage = Number(booking?.receipt?.allowedMileage ?? -1);
  const perExtraMileCost = Number(booking?.receipt?.perExtraMileCost || 0);
  const enteredMileage = Number(postTripMileage || 0);
  const computedExtraMileage = useMemo(() => {
    if (
      !isHost ||
      allowedMileage < 0 ||
      perExtraMileCost <= 0 ||
      !enteredMileage ||
      !preTripMileage
    ) {
      return 0;
    }
    return Math.max(0, Math.round(enteredMileage) - preTripMileage - allowedMileage);
  }, [allowedMileage, enteredMileage, isHost, perExtraMileCost, preTripMileage]);
  const appliedExtraMileage = overrideEnabled
    ? Math.max(0, Number(overrideMiles || 0))
    : computedExtraMileage;
  const estimatedCharge = Number((appliedExtraMileage * perExtraMileCost).toFixed(2));

  useEffect(() => {
    if (!isOpen) return;
    setStepIndex(0);
    setPostTripMileage(isHost ? String(booking?.postTripMileage || '') : '');
    setPostTripFuelLevel(isHost ? booking?.postTripFuelLevel || null : null);
    setOverrideEnabled(false);
    setOverrideMiles('');
    setSelectedFiles([]);
    setFilePreviews([]);
    setUploadProgress({});
    setCheckoutResult(null);
  }, [booking, isHost, isOpen]);

  useEffect(() => {
    return () => filePreviews.forEach(url => URL.revokeObjectURL(url));
  }, [filePreviews]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setSelectedFiles(prev => [...prev, ...acceptedFiles]);
    setFilePreviews(prev => [...prev, ...acceptedFiles.map(file => URL.createObjectURL(file))]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    multiple: true,
    disabled: isUploading,
  });

  const uploadFileToS3 = (file: File, index: number, s3Url: string, s3Key: string) =>
    new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', s3Url);
      xhr.setRequestHeader('Content-Type', file.type || 'image/jpeg');
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          setUploadProgress(prev => ({
            ...prev,
            [index]: Math.round((event.loaded * 100) / event.total),
          }));
        }
      };
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve(s3Key)
          : reject(new Error(`Upload failed with status ${xhr.status}`));
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(file);
    });

  const handleUploadNow = async () => {
    if (!selectedFiles.length) return;
    setIsUploading(true);
    try {
      const filesToUpload = selectedFiles.length;
      for (let index = 0; index < selectedFiles.length; index += 1) {
        const file = selectedFiles[index];
        const { data } = await getPresignedUrl({
          variables: {
            fileType: file.type || 'image/jpeg',
            bookingId: booking.id,
            imageType: 'tripEnd',
          },
        });
        const { url, key } = data.getPresignedUrlForBooking;
        await uploadFileToS3(file, index, url, key);
        await uploadTripPhoto({
          variables: {
            input: {
              bookingId: booking.id,
              imageUrl: key,
              imageType: 'tripEnd',
              photoTimestamp: new Date(),
            },
          },
        });
      }
      filePreviews.forEach(url => URL.revokeObjectURL(url));
      setSelectedFiles([]);
      setFilePreviews([]);
      setUploadProgress({});
      await refetchBooking();
      toast.success(`Successfully uploaded ${filesToUpload} return photo(s)`);
    } catch (error: any) {
      const msg = String(error?.message || 'Failed to upload photos');
      const friendly = /check-in window/i.test(msg)
        ? 'Trip start photos can only be uploaded during the check-in window. Open Start Trip when check-in is available.'
        : /trip end photos can only/i.test(msg)
          ? 'Return photos can only be uploaded during the allowed return window (from 3 hours before trip end through 90 days after).'
          : msg;
      toast.error(friendly);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteUploadedPhoto = async (imageUrl: string) => {
    try {
      await deleteTripPhoto({
        variables: {
          input: { bookingId: booking.id, imageUrl, imageType: 'tripEnd' },
        },
      });
      await refetchBooking();
      toast.success('Photo deleted');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete photo');
    }
  };

  const handleSubmit = async () => {
    if (currentStep === 'photos') {
      if (!canProceedFromPhotos) {
        toast.error(`Please upload at least ${MIN_PHOTOS} return photos`);
        return;
      }
      if (!isHost) {
        try {
          const result = await completeGuestCheckout({
            variables: {
              input: {
                bookingId: booking.id,
              },
            },
          });
          setCheckoutResult(result.data?.completeGuestCheckout);
          await refetchBooking();
          toast.success('Return submitted');
          onClose();
        } catch (error: any) {
          toast.error(error.message || 'Failed to complete return checkout');
        }
        return;
      }
      setStepIndex(1);
      return;
    }

    const mileageValue = Number(postTripMileage);
    if (!Number.isFinite(mileageValue) || mileageValue < 0) {
      toast.error('Please enter a valid ending mileage');
      return;
    }
    if (!postTripFuelLevel) {
      toast.error('Please select returned fuel level');
      return;
    }

    try {
      const variables: any = {
        input: {
          bookingId: booking.id,
          postTripMileage: Math.round(mileageValue),
          postTripFuelLevel,
        },
      };
      if (isHost) {
        if (overrideEnabled) {
          variables.input.firstPriorityHostCalcExMileage = Math.max(
            0,
            Math.round(Number(overrideMiles || 0)),
          );
        } else if (booking?.preTripMileage == null) {
          variables.input.firstPriorityHostCalcExMileage = 0;
        }
      }
      const result = await completeHostCheckout({ variables });
      const completed = result.data?.completeHostCheckout;
      setCheckoutResult(completed);
      await refetchBooking();
      toast.success('Return checkout completed');
      // Without extra mileage to charge, the charge banner is hidden; `!checkoutResult` also hides
      // primary actions — close so the host is not stuck on an empty modal after success.
      const extraCostAfter = Number(
        completed?.receipt?.extraMilagesCost ??
          booking?.receipt?.extraMilagesCost ??
          estimatedCharge ??
          0,
      );
      if (extraCostAfter <= 0) {
        onClose();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to complete return checkout');
    }
  };

  const handleChargeExtraMileage = async () => {
    try {
      const { data } = await chargeExtraMileage({ variables: { bookingId: booking.id } });
      await refetchBooking();
      const result = data?.chargeExtraMileage;
      toast.success(result?.message || 'Extra mileage charge processed');
      if (result?.paymentLink) {
        window.open(result.paymentLink, '_blank', 'noopener,noreferrer');
      }
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to charge extra mileage');
    }
  };

  const extraCost = Number(
    checkoutResult?.receipt?.extraMilagesCost ??
      booking?.receipt?.extraMilagesCost ??
      estimatedCharge ??
      0,
  );
  const extraMiles = Number(checkoutResult?.receipt?.extraMilages ?? appliedExtraMileage ?? 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} modalWidth="min(720px, calc(100vw - 20px))">
      <Container>
        <Title>{isHost ? 'Checkout Guest' : 'End Trip'}</Title>
        <StepHeader>
          Step {stepIndex + 1} of {steps.length}
        </StepHeader>

        {currentStep === 'photos' && (
          <Section>
            <Label>After-trip photos</Label>
            <>
              <HelperText>
                Upload at least {MIN_PHOTOS} return photos. Host and guest photos are shown for
                reference.
              </HelperText>
              <HelperText>
                {totalOwnPhotoCount} of {MIN_PHOTOS} required photos ready for your checkout.
              </HelperText>
            </>
            <DropzoneContainer {...getRootProps()} isDragActive={isDragActive}>
              <input {...getInputProps()} />
              <p style={{ margin: 0, fontWeight: 600 }}>
                {isDragActive ? 'Drop photos here' : 'Click or drag photos to select'}
              </p>
              <HelperText>Upload selected photos before continuing.</HelperText>
            </DropzoneContainer>
            {(uploadedTripEndImages.length > 0 || selectedFiles.length > 0) && (
              <PhotoGrid>
                {uploadedTripEndImages.map((image: any, index: number) => {
                  const imageUrl = image.url?.startsWith('http')
                    ? image.url
                    : `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${image.url}`;
                  return (
                    <PhotoPreview key={`uploaded-${image.url || index}`}>
                      <PhotoImg src={imageUrl} alt={`Return photo ${index + 1}`} />
                      {(isHost ||
                        (actorUserId != null &&
                          sameBookingUserId(image.uploadedBy, actorUserId))) && (
                        <RemoveButton
                          disabled={isUploading}
                          onClick={() => handleDeleteUploadedPhoto(image.url)}
                        >
                          x
                        </RemoveButton>
                      )}
                    </PhotoPreview>
                  );
                })}
                {filePreviews.map((preview, index) => (
                  <PhotoPreview key={`pending-${preview}`}>
                    <PhotoImg src={preview} alt={`Pending photo ${index + 1}`} />
                    <RemoveButton
                      disabled={isUploading}
                      onClick={() => {
                        URL.revokeObjectURL(preview);
                        setSelectedFiles(files => files.filter((_, i) => i !== index));
                        setFilePreviews(previews => previews.filter((_, i) => i !== index));
                      }}
                    >
                      x
                    </RemoveButton>
                    {uploadProgress[index] !== undefined && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          height: 4,
                          width: `${uploadProgress[index]}%`,
                          background: '#4F46E5',
                        }}
                      />
                    )}
                  </PhotoPreview>
                ))}
              </PhotoGrid>
            )}
            {selectedFiles.length > 0 && (
              <Button
                text={isUploading ? 'Uploading...' : 'Upload Now'}
                onClick={handleUploadNow}
                isLoading={isUploading}
                isDisabled={isUploading}
                buttonWidth="180px"
              />
            )}
          </Section>
        )}

        {isHost && currentStep === 'details' && (
          <Section>
            <Label>{km ? 'End trip odometer (km)' : 'End trip mileage (miles)'}</Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={postTripMileage}
              onChange={event => setPostTripMileage(event.target.value.replace(/[^0-9]/g, ''))}
              placeholder="Odometer at return"
            />
            <Label>Returned fuel level</Label>
            <FuelGrid>
              {FUEL_LEVELS.map(level => (
                <FuelButton
                  key={level}
                  selected={postTripFuelLevel === level}
                  onClick={() => setPostTripFuelLevel(level)}
                >
                  {level}%
                </FuelButton>
              ))}
            </FuelGrid>
            {isHost && (
              <>
                <SummaryBox>
                  <div>
                    {km ? 'Starting odometer' : 'Starting mileage'}:{' '}
                    {booking.preTripMileage ?? 'Not recorded'}
                  </div>
                  <div>
                    {km ? 'Allowed distance' : 'Allowed mileage'}:{' '}
                    {allowedMileage < 0 ? 'Unlimited' : `${allowedMileage} ${km ? 'km' : 'miles'}`}
                  </div>
                  <div>
                    {km
                      ? `Computed extra distance: ${computedExtraMileage} km`
                      : `Computed extra miles: ${computedExtraMileage}`}
                  </div>
                  <div>
                    {km ? 'Estimated extra distance charge' : 'Estimated extra mileage charge'}:{' '}
                    {money(estimatedCharge)}
                  </div>
                </SummaryBox>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={overrideEnabled}
                    onChange={event => setOverrideEnabled(event.target.checked)}
                  />
                  {km
                    ? 'Override computed extra distance (optional)'
                    : 'Override computed extra miles (optional)'}
                </label>
                {overrideEnabled && (
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={overrideMiles}
                    onChange={event => setOverrideMiles(event.target.value.replace(/[^0-9]/g, ''))}
                    placeholder={
                      km ? 'Extra km to bill (0 waives)' : 'Extra miles to bill (0 waives)'
                    }
                  />
                )}
              </>
            )}
          </Section>
        )}

        {isHost && checkoutResult && extraCost > 0 && (
          <ChargeBanner>
            <strong>
              {km
                ? `Extra distance: ${extraMiles} km - ${money(extraCost)}`
                : `Extra mileage: ${extraMiles} miles - $${extraCost.toFixed(2)}`}
            </strong>
            <span>Charge guest now?</span>
            <Actions>
              <Button text="Skip" theme="outlined" onClick={onClose} />
              <Button
                text={chargingMileage ? 'Charging...' : 'Charge Now'}
                onClick={handleChargeExtraMileage}
                isLoading={chargingMileage}
                isDisabled={chargingMileage}
              />
            </Actions>
          </ChargeBanner>
        )}

        {!checkoutResult && (
          <Actions>
            {stepIndex > 0 && (
              <Button text="Back" theme="outlined" onClick={() => setStepIndex(stepIndex - 1)} />
            )}
            <Button
              text={
                currentStep === 'photos'
                  ? isHost
                    ? 'Next'
                    : 'Complete End Trip'
                  : 'Complete Checkout'
              }
              onClick={handleSubmit}
              isDisabled={
                (currentStep === 'photos' && !canProceedFromPhotos) ||
                completingHost ||
                completingGuest
              }
              isLoading={completingHost || completingGuest}
            />
          </Actions>
        )}
      </Container>
    </Modal>
  );
};
